import { ClockIcon } from '@heroicons/react/24/outline'
import { useRef } from 'react'
import type { Color } from 'stories/types'

import { Tooltip } from '../Tooltip/Tooltip'

interface ToggleButtonProps {
  /**
   * Id of Input
   */
  id: string
  /**
   * Which one is selected
   */
  value?: boolean
  /**
   * button label
   */
  label?: string[]
  /**
   * optional title
   */
  title?: string
  /**
   * What background color to use
   */
  color?: Color
  /**
   * How large should the button be?
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Error of Input
   */
  error?: string
  /**
   * Optional history handler
   */
  history?: boolean

  additionalElements?: JSX.Element | Function | null
  /**
   * Custom class name
   */
  className?: string

  btnClassName?: string
  /**
   * Show text on left
   */
  textLeft?: boolean
  /**
   * Optional click handler
   */
  onChange?: (checked: any) => void
  onBlur?: () => void
  showHistory?: () => void
}

export const ToggleButton = ({
  id = '',
  label = ['Yes', 'No'],
  title,
  size = 'sm',
  disabled = false,
  error = '',
  tooltip = '',
  history = false,
  additionalElements,
  value,
  className = '',
  btnClassName = '',
  textLeft = false,
  onChange = () => {},
  onBlur = () => {},
  showHistory = () => {},
}: ToggleButtonProps) => {
  const classes1 = [
    `border border-shade-blue`,
    value === true ? `bg-shade-blue text-white` : `bg-gray-200 opacity-60 text-gray-500`,
    `font-${size}`,
    `text-${size}`,
    'px-2',
    'py-1.5',
    'items-center justify-center',
    'min-w-[2.75rem]',
    'whitespace-nowrap',
    'h-9',
    disabled && 'cursor-not-allowed',
    btnClassName,
  ]
  const classes2 = [
    `border border-shade-blue`,
    `border-l-0`,
    value === false ? `bg-shade-blue text-white` : `bg-gray-200 opacity-60 text-gray-500`,
    `font-${size}`,
    `text-${size}`,
    'px-2',
    'py-1.5',
    'items-center justify-center',
    'min-w-[2.75rem]',
    'whitespace-nowrap',
    'h-9',
    disabled && 'cursor-not-allowed',
    btnClassName,
  ]

  const textComponent = (
    <span className={`${textLeft ? 'mr' : 'ml'}-3 text-[14.5px] font-medium text-gray-900 dark:text-gray-300`}>
      {title}
    </span>
  )

  const changeValue = (value: boolean) => {
    onChange(value)
    setTimeout(() => {
      inputRef?.current?.click()
    }, 250)
  }

  const inputRef = useRef<HTMLDivElement>(null)

  return (
    <div className={`${className}`} id={id}>
      <div className={`flex items-center group relative`}>
        {textLeft ? textComponent : null}
        <div className="flex">
          <button type={'button'} className={classes1.join(' ')} disabled={disabled} onClick={() => changeValue(true)}>
            <span>{label[0]}</span>
          </button>
          <button type={'button'} className={classes2.join(' ')} disabled={disabled} onClick={() => changeValue(false)}>
            <span>{label[1]}</span>
          </button>
        </div>
        {!textLeft ? textComponent : null}
        {tooltip.length > 0 ? (
          <div className="ml-2">
            <Tooltip message={tooltip}></Tooltip>
          </div>
        ) : null}
        {additionalElements !== null &&
          (typeof additionalElements == 'function' ? additionalElements(onChange) : additionalElements)}
        {history && (
          <div className="flex flex-none w-5">
            <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
            </span>
          </div>
        )}
      </div>
      <div ref={inputRef} className="hidden" onClick={onBlur} />
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1">{error}</p>}
    </div>
  )
}
